import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./course-sections.component.scoped.scss";
import { Box, Tooltip, Typography, withStyles } from "@material-ui/core";
import ActionMenuComponent from "../../../../reusable-components/components/menu.component";
import { useTranslation } from "react-i18next";
import {
  SectionVideoStatusLabel,
  sectionVideoUserStatusLabels,
  sectionVideoAdminStatusLabels,
  SectionFilter,
  SectionStatus,
} from "../../../types/section-videos.types";
import AppFilterChipComponent from "../../../../../components/form/app-filter-chip";
import { UserRoles } from "../../../../../types/userRoles";
import { GlobalContext } from "../../../../../store";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import { services } from "../../../../../services";
import CreateLessonDialog from "../../../modals/create-lesson/create-lesson.dialog";
import { CreateSectionDialog } from "../../../modals/create-section/create-section.dialog";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog/confirm-dialog";
import { CreateCheckpointDialog } from "../../../modals/create-checkpoint/create-checkpoint.dialog";
import LessonCardComponent from "../../../shared/lesson-card/lesson-card.component";
import CheckpointCardComponent from "../../../shared/checkpoint-card/checkpoint-card.component";
import { Skeleton } from "@material-ui/lab";

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "var(--accent-color)",
    color: "#ffffff",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

type ModalType =
  | "CREATE_LESSON"
  | "EDIT_LESSON"
  | "CREATE_CHECKPOINT"
  | "EDIT_CHECKPOINT"
  | "EDIT_SECTION"
  | "DELETE_SECTION"
  | "DELETE_LESSON"
  | "DELETE_CHECKPOINT"
  | null;

const buildSectionActions = ({
  t,
  section,
  setActiveModal,
  setSelectedItem,
}: any) => [
  {
    label: t("EDUCATION_PAGE.EDIT_SECTION"),
    onClick: () => {
      setSelectedItem(section);
      setActiveModal("EDIT_SECTION");
    },
  },
  {
    label: t("EDUCATION_PAGE.DELETE_SECTION"),
    onClick: () => {
      setSelectedItem(section);
      setActiveModal("DELETE_SECTION");
    },
  },
  {
    label: t("EDUCATION_PAGE.CREATE_A_VIDEO"),
    onClick: () => {
      setSelectedItem(section);
      setActiveModal("CREATE_LESSON");
    },
  },
  {
    label: t("EDUCATION_PAGE.CREATE_A_CHECKPOINT"),
    disabled: section.checkpoints?.length >= 1,
    onClick: () => {
      setSelectedItem(section);
      setActiveModal("CREATE_CHECKPOINT");
    },
  },
];

const CourseSectionsComponent = ({
  courseId,
  sections,
  loading,
  onRefetchSections,
  getSectionFilter,
  onSectionFilterChange,
}: {
  courseId: string;
  sections: any[];
  loading: boolean;
  onRefetchSections: () => void;
  getSectionFilter: (sectionId: number) => SectionFilter;
  onSectionFilterChange: (
    sectionId: number,
    partial: Partial<SectionFilter>,
  ) => void;
}) => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);

  const hasFullAccess = useMemo(
    () => UserRoles.FULL_ACCESS_ROLES.includes(user.role),
    [user.role],
  );

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  useEffect(() => {
    setSelectedItem(null);
  }, [sections]);

  const handleDelete = async ({
    service,
    id,
    successMessage,
  }: {
    service: (id: string) => Promise<any>;
    id: string;
    successMessage: string;
  }) => {
    try {
      const response = await service(id);

      if (response?.message === "SUCCESS") {
        showToastMsg("success", successMessage);
        onRefetchSections();
        return;
      }

      showPopupToastMsg(
        "error",
        t(response?.message || "SOME_THING_WENT_WRONG"),
      );
    } catch {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  // get lesson from section object
  const getSectionLessons = useCallback((section: any) => {
    const sectionlessons = section.lessons || [];
    return sectionlessons;
  }, []);

  const getSectionCheckpoints = useCallback((section: any) => {
    const sectioncheckpoints = section.checkpoints || [];
    return sectioncheckpoints;
  }, []);

  // handle close Edit section dialog
  const closeModal = (shouldRefetch?: boolean) => {
    setActiveModal(null);
    setSelectedItem(null);

    if (shouldRefetch) {
      onRefetchSections();
    }
  };

  const handleDeleteConfirm = async (
    confirmed: boolean,
    service: (id: string) => Promise<any>,
    successMessage: string,
  ) => {
    closeModal();
    if (!confirmed || !selectedItem?.id) {
      return;
    }
    await handleDelete({
      service,
      id: selectedItem.id,
      successMessage,
    });
  };

  return (
    <Box className="sections__content">
      {loading ? (
        <Skeleton variant="text" width={250} height={18} className="my-4" />
      ) : (
        <Typography variant="h2">{t("EDUCATION_PAGE.VIDEO_LIST")}</Typography>
      )}
      <Box className="global__box__content p-0">
        {sections.map((section) => {
          const sectionFilter = getSectionFilter(section.id);
          return (
            <Box className="sections__list__item" key={section.id}>
              <Box className="global__box__content__header">
                <Box
                  className="info d-flex align-items-center"
                  style={{ gap: "1.5rem" }}
                >
                  <Typography variant="h3">{section.title}</Typography>
                  <Typography variant="caption">
                    {getSectionLessons(section).length} Videos |{" "}
                    {getSectionCheckpoints(section).length} Checkpoints
                  </Typography>
                </Box>
                {!hasFullAccess &&
                  section.userStatus?.status !== SectionStatus.NOT_STARTED && (
                    <Box className="section__badge">
                      <span
                        style={{
                          backgroundColor:
                            section.userStatus?.status ===
                            SectionStatus.IN_PROGRESS
                              ? "var(--warning)"
                              : "var(--success)",
                        }}
                      >
                        {section.userStatus?.status ===
                        SectionStatus.IN_PROGRESS
                          ? t("IN_PROGRESS")
                          : t("ACHIEVED")}
                      </span>
                    </Box>
                  )}
                {!hasFullAccess && section.locked && (
                  <Box className="section__badge" style={{ cursor: "pointer" }}>
                    <StyledTooltip
                      title={
                        t(
                          "EDUCATION_PAGE.COMPLETE_ALL_PREVIOUS_SECTIONS_TO_UNLOCK_THIS_SECTION",
                        ) as string
                      }
                      placement="left"
                    >
                      <span style={{ backgroundColor: "var(--gray)" }}>
                        {t("LOCKED")}
                      </span>
                    </StyledTooltip>
                  </Box>
                )}
                {hasFullAccess && (
                  <Box className="global__box__content__action">
                    <ActionMenuComponent
                      items={buildSectionActions({
                        t,
                        section,
                        setActiveModal,
                        setSelectedItem,
                      })}
                    />
                  </Box>
                )}
              </Box>
              <Box className="global__box__content__body">
                <Box
                  className="d-flex align-items-center"
                  style={{ gap: "1.9rem", marginBottom: "15px" }}
                >
                  <Typography variant="body2">Videos</Typography>
                  <Box
                    className="d-flex align-items-center"
                    style={{ gap: "1rem" }}
                  >
                    {(hasFullAccess
                      ? sectionVideoAdminStatusLabels
                      : sectionVideoUserStatusLabels
                    ).map((label: SectionVideoStatusLabel) => (
                      <AppFilterChipComponent
                        key={label.value}
                        label={t(label.label as string)}
                        size="small"
                        selected={
                          sectionFilter[
                            hasFullAccess ? "lessonStatus" : "userStatus"
                          ] === label.value
                        }
                        onClick={() =>
                          onSectionFilterChange(section.id, {
                            [hasFullAccess
                              ? "lessonStatus"
                              : "userStatus"]: label.value,
                          })
                        }
                      />
                    ))}
                  </Box>
                </Box>
                {section.lessons && section.lessons.length ? (
                  <Box className="lessons__list mb-4">
                    {getSectionLessons(section).map((lesson: any) => (
                      <LessonCardComponent
                        key={lesson.id}
                        lesson={lesson}
                        loading={loading}
                        hasFullAccess={hasFullAccess}
                        sectionLocked={section.locked}
                        onEdit={() => {
                          setActiveModal("EDIT_LESSON");
                          setSelectedItem(lesson);
                        }}
                        onDelete={() => {
                          setActiveModal("DELETE_LESSON");
                          setSelectedItem(lesson);
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
                {section.checkpoints && section.checkpoints.length ? (
                  <Box className="checkpoints__list mb-4">
                    {getSectionCheckpoints(section).map((checkpoint: any) => (
                      <CheckpointCardComponent
                        key={checkpoint.id}
                        checkpoint={checkpoint}
                        loading={loading}
                        hasFullAccess={hasFullAccess}
                        sectionLocked={section.locked}
                        onEdit={() => {
                          setActiveModal("EDIT_CHECKPOINT");
                          setSelectedItem(checkpoint);
                        }}
                        onDelete={() => {
                          setActiveModal("DELETE_CHECKPOINT");
                          setSelectedItem(checkpoint);
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
                {!section.lessons?.length && !section.checkpoints?.length ? (
                  <Box className="text-center my-5">
                    <Typography variant="h3" style={{ color: "#78829D" }}>
                      {t("NO_CONTENT_FOUND")}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        })}
        {!loading && sections.length === 0 ? (
          <Box className="text-center my-5">
            <Typography variant="h3" style={{ color: "#78829D" }}>
              {t("NO_CONTENT_FOUND")}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {activeModal === "EDIT_SECTION" && (
        <CreateSectionDialog
          show={activeModal === "EDIT_SECTION"}
          onClose={closeModal}
          courseId={Number(courseId)}
          sectionId={Number(selectedItem.id)}
          sectionData={selectedItem}
        />
      )}
      {activeModal === "CREATE_LESSON" && (
        <CreateLessonDialog
          show={activeModal === "CREATE_LESSON"}
          onClose={closeModal}
          sectionId={selectedItem.id}
          courseId={Number(courseId)}
        />
      )}
      {activeModal === "EDIT_LESSON" && (
        <CreateLessonDialog
          show={activeModal === "EDIT_LESSON"}
          onClose={closeModal}
          lessonId={Number(selectedItem.id)}
          lessonData={selectedItem}
          courseId={Number(courseId)}
        />
      )}
      {activeModal === "CREATE_CHECKPOINT" && (
        <CreateCheckpointDialog
          show={activeModal === "CREATE_CHECKPOINT"}
          onClose={closeModal}
          sectionId={Number(selectedItem.id)}
          courseId={Number(courseId)}
        />
      )}
      {activeModal === "EDIT_CHECKPOINT" && (
        <CreateCheckpointDialog
          show={activeModal === "EDIT_CHECKPOINT"}
          onClose={closeModal}
          courseId={Number(courseId)}
          checkpointId={Number(selectedItem.id)}
          checkpointData={selectedItem}
        />
      )}
      {activeModal === "DELETE_SECTION" && (
        <ConfirmDialog
          show={activeModal === "DELETE_SECTION"}
          onClose={(confirmed: boolean) =>
            handleDeleteConfirm(
              confirmed,
              services.courses.deleteSection,
              t("EDUCATION_PAGE.SECTION_DELETED_SUCCESSFULLY"),
            )
          }
          dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE_SECTION")}
          dialogBody={t(
            "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_SECTION",
          )}
          confirmBtnText={t("DELETE")}
          cancelBtn={true}
        />
      )}
      {activeModal === "DELETE_LESSON" && (
        <ConfirmDialog
          show={activeModal === "DELETE_LESSON"}
          onClose={(confirmed: boolean) =>
            handleDeleteConfirm(
              confirmed,
              services.courses.deleteLesson,
              t("EDUCATION_PAGE.LESSON_DELETED_SUCCESSFULLY"),
            )
          }
          dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE")}
          dialogBody={t(
            "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_LESSON",
          )}
          confirmBtnText={t("DELETE")}
          cancelBtn={true}
        />
      )}
      {activeModal === "DELETE_CHECKPOINT" && (
        <ConfirmDialog
          show={activeModal === "DELETE_CHECKPOINT"}
          onClose={(confirmed: boolean) =>
            handleDeleteConfirm(
              confirmed,
              services.courses.deleteCheckpoint,
              t("EDUCATION_PAGE.CHECKPOINT_DELETED_SUCCESSFULLY"),
            )
          }
          dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE")}
          dialogBody={t(
            "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_CHECKPOINT",
          )}
          confirmBtnText={t("DELETE")}
          cancelBtn={true}
        />
      )}
    </Box>
  );
};

export default CourseSectionsComponent;
